import initialState from "../store/initial-state";
import {
  CLEAR_RIGHTS_ASSIGN_ERROR,
  GET_RIGHTS_LIST,
  GET_RIGHTS_LIST_SUCCESS,
  GET_RIGHTS_LIST_FAILURE,
  GET_RIGHTS_TREE_NODE,
  GET_RIGHTS_TREE_NODE_SUCCESS,
  GET_RIGHTS_TREE_NODE_FAILURE,
  GET_RIGHTS_SUMMARY,
  GET_RIGHTS_SUMMARY_SUCCESS,
  GET_RIGHTS_SUMMARY_FAILURE,
  ASSIGN_RIGHTS_MANY,
  ASSIGN_RIGHTS_MANY_SUCCESS,
  ASSIGN_RIGHTS_MANY_FAILURE,
  REVERT_RIGHTS_OPERATION,
  REVERT_RIGHTS_OPERATION_SUCCESS,
  REVERT_RIGHTS_OPERATION_FAILURE,
  EXPORT_RIGHTS_REPORT,
  EXPORT_RIGHTS_REPORT_SUCCESS,
  EXPORT_RIGHTS_REPORT_FAILURE,
  CLEAR_RIGHTS_LAST_OPERATION,
  OPEN_RIGHTS_VIEWER,
  OPEN_RIGHTS_VIEWER_SUCCESS,
  OPEN_RIGHTS_VIEWER_FAILURE,
  CLOSE_RIGHTS_VIEWER
} from "../constants/rights-report-types";
import { ASSET_RIGHTS_STATE_UPDATED } from "../constants/rights-constants";

const emptyNode = { loading: false, error: false, folders: [], files: [] };

// Derechos guardados desde el visor del Gestor (CollectionMiddleViewAssetInfo
// despacha ASSET_RIGHTS_STATE_UPDATED). Antes este reducer no lo escuchaba: el
// visor seguía mostrando el estado viejo y el árbol sólo se enteraba
// recargándose entero al cerrar.
const patchRow = (row, p) =>
  row && row._id === p.assetId
    ? {
        ...row,
        rightsState: p.rightsState,
        rightsStatus: p.rightsStatus,
        dueDate: p.dueDate,
        createdDate: p.createdDate
      }
    : row;
const patchAsset = (asset, p) =>
  asset && asset._id === p.assetId
    ? {
        ...asset,
        rightsState: p.rightsState,
        metadata: {
          ...asset.metadata,
          rightsStatus: p.rightsStatus,
          dueDate: p.dueDate,
          createdDate: p.createdDate,
          licenseFile: p.licenseFile,
          rightsAssignedAt: p.rightsAssignedAt
        }
      }
    : asset;

export default (state = initialState.rights, action) => {
  switch (action.type) {
    case OPEN_RIGHTS_VIEWER:
      return { ...state, viewer: { ...state.viewer, isFetching: true, error: false } };

    case OPEN_RIGHTS_VIEWER_SUCCESS:
      return {
        ...state,
        viewer: {
          ...state.viewer,
          isFetching: false,
          error: false,
          // Mismo contrato que el resto de las colecciones: un array con el
          // elegido marcado. CollectionMiddleView lo busca con `isSelected` y
          // las flechas se mueven dentro de este array, así que vienen los
          // hermanos de la carpeta y no sólo la pieza abierta.
          data: action.payload.data
        }
      };

    case OPEN_RIGHTS_VIEWER_FAILURE:
      return { ...state, viewer: { ...state.viewer, isFetching: false, error: true, data: [] } };

    case CLOSE_RIGHTS_VIEWER:
      return { ...state, viewer: { ...state.viewer, isFetching: false, error: false, data: [] } };

    case CLEAR_RIGHTS_ASSIGN_ERROR:
      return { ...state, errorAssign: false, errorAssignCode: null };

    case ASSET_RIGHTS_STATE_UPDATED: {
      const p = action.payload;
      const nodes = {};
      Object.keys(state.nodes || {}).forEach(key => {
        const node = state.nodes[key];
        nodes[key] = node && node.files
          ? { ...node, files: node.files.map(row => patchRow(row, p)) }
          : node;
      });
      return {
        ...state,
        nodes,
        list: state.list && state.list.rows
          ? { ...state.list, rows: state.list.rows.map(row => patchRow(row, p)) }
          : state.list,
        viewer: {
          ...state.viewer,
          data: (state.viewer.data || []).map(asset => patchAsset(asset, p))
        }
      };
    }

    case GET_RIGHTS_LIST:
      return { ...state, list: { ...state.list, loading: true, error: false } };

    case GET_RIGHTS_LIST_SUCCESS:
      return {
        ...state,
        list: {
          ...state.list,
          loading: false,
          error: false,
          rows: action.payload.rows,
          total: action.payload.total,
          page: action.payload.page,
          limit: action.payload.limit,
          orderBy: action.payload.orderBy
        }
      };

    case GET_RIGHTS_LIST_FAILURE:
      // Se conservan las filas anteriores: vaciar la tabla ante un error de red
      // deja al usuario sin contexto y sin saber si el filtro no devolvió nada.
      return { ...state, list: { ...state.list, loading: false, error: true } };

    case GET_RIGHTS_TREE_NODE:
      return {
        ...state,
        nodes: {
          ...state.nodes,
          [action.payload.nodeKey]: {
            ...emptyNode,
            ...state.nodes[action.payload.nodeKey],
            // Queda como la petición vigente de este nodo: las respuestas que
            // lleguen con otro id son de un filtro anterior y se descartan.
            requestId: action.payload.requestId,
            // Silencioso: el nodo sigue mostrando lo que tenía mientras llega
            // lo nuevo (cierre del visor). El requestId igual se actualiza.
            loading: action.payload.silent
              ? Boolean((state.nodes[action.payload.nodeKey] || {}).loading)
              : true,
            error: false
          }
        }
      };
    case GET_RIGHTS_TREE_NODE_SUCCESS: {
      // Llegó una respuesta que ya no es la última pedida para este nodo: se
      // descarta. Sin esto, dos cambios de filtro seguidos dejaban el árbol con
      // el resultado del filtro anterior si su petición respondía después.
      const vigente = (state.nodes[action.payload.nodeKey] || {}).requestId;
      if (
        action.payload.requestId &&
        vigente &&
        action.payload.requestId !== vigente
      ) {
        return state;
      }
      return {
        ...state,
        nodes: {
          ...state.nodes,
          [action.payload.nodeKey]: {
            loading: false,
            error: false,
            requestId: vigente,
            folders: action.payload.folders,
            files: action.payload.files
          }
        }
      };
    }
    case GET_RIGHTS_TREE_NODE_FAILURE: {
      // Mismo criterio que el success: el fallo de una petición que ya quedó
      // obsoleta no debe marcar en error al nodo que está cargando otra cosa.
      const vigenteErr = (state.nodes[action.payload.nodeKey] || {}).requestId;
      if (
        action.payload.requestId &&
        vigenteErr &&
        action.payload.requestId !== vigenteErr
      ) {
        return state;
      }
      return {
        ...state,
        nodes: {
          ...state.nodes,
          [action.payload.nodeKey]: {
            ...emptyNode,
            ...state.nodes[action.payload.nodeKey],
            loading: false,
            error: true
          }
        }
      };
    }

    case GET_RIGHTS_SUMMARY:
      if (action.payload && action.payload.silent) return state;
      return { ...state, loadingSummary: true, errorSummary: false };
    case GET_RIGHTS_SUMMARY_SUCCESS:
      return {
        ...state,
        loadingSummary: false,
        errorSummary: false,
        summary: {
          ...state.summary,
          ...action.payload.summary,
          total: action.payload.total || 0,
          outOfModule: action.payload.outOfModule || 0,
          updatedAt: action.payload.updatedAt
        }
      };
    case GET_RIGHTS_SUMMARY_FAILURE:
      return { ...state, loadingSummary: false, errorSummary: true };

    case ASSIGN_RIGHTS_MANY:
      return {
        ...state,
        loadingAssign: true,
        errorAssign: false,
        errorAssignCode: null
      };
    case ASSIGN_RIGHTS_MANY_SUCCESS: {
      // Un 200 NO significa que se haya aplicado algo. El lote es de éxito
      // parcial a propósito —informa `updated` y `skippedCount` en vez de
      // fallar entero—, pero acá se daba por buena cualquier respuesta, así
      // que "no se aplicó a ninguno" se veía igual que un éxito: la selección
      // se limpiaba, el árbol se recargaba idéntico y no aparecía ni un aviso.
      const resultado = action.payload.result || {};
      const nadaCambio = Number(resultado.updated) === 0;

      if (nadaCambio) {
        // Tres motivos distintos para un `updated: 0`, y al usuario le importan
        // como cosas distintas:
        //   NothingUpdated  quedaron piezas fuera -> es un problema de permisos
        //   NoChanges       la escritura corrió (hay operationId) pero Mongo no
        //                   modificó nada: ya estaban en ese estado. No es error
        //   EmptySelection  la selección no alcanzó ninguna pieza de contenido
        return {
          ...state,
          loadingAssign: false,
          errorAssign: true,
          errorAssignCode:
            resultado.skippedCount > 0
              ? "NothingUpdated"
              : resultado.operationId
              ? "NoChanges"
              : "EmptySelection"
        };
      }

      return {
        ...state,
        loadingAssign: false,
        errorAssign: false,
        errorAssignCode: null,
        // Se conserva el parcial para poder decir "se aplicó a N, quedaron M
        // fuera" en vez de un éxito liso que oculta la mitad de lo que pasó.
        lastOperation: resultado.operationId
          ? {
              operationId: resultado.operationId,
              updated: resultado.updated,
              skippedCount: resultado.skippedCount || 0,
              at: new Date().toISOString()
            }
          : state.lastOperation
      };
    }
    case ASSIGN_RIGHTS_MANY_FAILURE:
      // Se guarda el `code` del backend, no sólo un booleano: sin él todos los
      // fallos se muestran como "no se pudo aplicar" y el usuario no sabe si el
      // problema es la red, un permiso o que la selección es demasiado grande.
      return {
        ...state,
        loadingAssign: false,
        errorAssign: true,
        errorAssignCode:
          (action.payload &&
            action.payload.data &&
            action.payload.data.code) ||
          null
      };

    case REVERT_RIGHTS_OPERATION:
      return { ...state, loadingRevert: true, errorRevert: false };
    case REVERT_RIGHTS_OPERATION_SUCCESS:
      return {
        ...state,
        loadingRevert: false,
        errorRevert: false,
        lastOperation: null
      };
    case REVERT_RIGHTS_OPERATION_FAILURE:
      return { ...state, loadingRevert: false, errorRevert: true };

    case EXPORT_RIGHTS_REPORT:
      return { ...state, loadingExport: true, errorExport: false };
    case EXPORT_RIGHTS_REPORT_SUCCESS:
      return { ...state, loadingExport: false, errorExport: false };
    case EXPORT_RIGHTS_REPORT_FAILURE:
      return { ...state, loadingExport: false, errorExport: true };

    case CLEAR_RIGHTS_LAST_OPERATION:
      return { ...state, lastOperation: null };

    default:
      return state;
  }
};
